import get from "lodash/get";
import isEqual from "lodash/isEqual";
import { getRoleFormKeyPrefix } from "PFApp/components/activity_form/multi_activity_form/tabular_roles_form/tabular_roles_form.utils";
import {
  FIELD_ID_KEY,
  FIELD_PARENT_ACTIVITY_KEY,
  FIELD_SUBTEMPLATE_KEY,
  FORM_ROLES_KEY,
  FORM_SUBTEMPLATES_KEY,
  NO_SUBTEMPLATE_VALUE
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { FormSchemaField, RolesFormKey } from "PFApp/types/activity_form/activity_form_schema";
import {
  ManageRolesFormType,
  ManageRolesMultiForm
} from "PFApp/types/activity_form/manage_roles_form_schema";
import { Id } from "PFCore/types";
import { FieldPath } from "react-hook-form";

import { isEmptyValue } from "./hooks/use_form_unsaved_edits_tracker.utils";
import { DRAFT_UNIQUE_ID_PREFIX } from "./manage_roles_constants";

export const getSubtemplateIndex = (rolesFormKey: RolesFormKey) => parseInt(rolesFormKey.split(".")[1], 10);

export const buildRoleFieldPath = (roleFormKey: string, fieldKey: string) =>
  `${roleFormKey}.${fieldKey}` as FieldPath<ManageRolesMultiForm>;

export const isManageRolesAddRoleEnabled = (): boolean =>
  localStorage.getItem("manage_roles_add_role_enabled") === "true";

export const groupRolesIntoForm = (roles: ManageRolesFormType[]): ManageRolesMultiForm => {
  const subtemplateMap = new Map<string, ManageRolesFormType[]>();

  for (const role of roles) {
    const key = role.subtemplate_key ?? NO_SUBTEMPLATE_VALUE;
    if (!subtemplateMap.has(key)) {
      subtemplateMap.set(key, []);
    }
    subtemplateMap.get(key)!.push(role);
  }

  return {
    [FORM_SUBTEMPLATES_KEY]: Array.from(subtemplateMap.entries()).map(([key, subtemplateRoles]) => ({
      [FIELD_SUBTEMPLATE_KEY]: key,
      [FORM_ROLES_KEY]: subtemplateRoles
    }))
  };
};

const MANAGE_ROLES_BASE_COLUMNS_ORDER = [
  FIELD_PARENT_ACTIVITY_KEY,
  "name",
  "original_wm",
  "suggested_wm",
  "booked_wm"
] as const;

export const getManageRolesColumnsOrder = (selectedColumnNames: string[]): string[] => {
  const anchorIndex = MANAGE_ROLES_BASE_COLUMNS_ORDER.indexOf("name");

  return [
    ...MANAGE_ROLES_BASE_COLUMNS_ORDER.slice(0, anchorIndex + 1),
    ...selectedColumnNames,
    ...MANAGE_ROLES_BASE_COLUMNS_ORDER.slice(anchorIndex + 1)
  ];
};

export const mergeManageRolesColumns = <T extends { id?: string }, U extends { id?: string }>(
  schemaColumns: T[],
  customColumns: U[],
  columnsOrder: readonly string[]
): (T | U)[] => {
  const allColumns = [...customColumns, ...schemaColumns];
  const columnMap = new Map(allColumns.filter((col) => col.id !== undefined).map((col) => [col.id!, col]));

  const orderedColumns: (T | U)[] = [];

  // First: add explicitly ordered columns that exist
  for (const id of columnsOrder) {
    const col = columnMap.get(id);
    if (col) {
      orderedColumns.push(col);
      columnMap.delete(id);
    }
  }

  // Then: append remaining schema columns not in the order list
  for (const col of schemaColumns) {
    if (col.id !== undefined && columnMap.has(col.id)) {
      orderedColumns.push(col);
      columnMap.delete(col.id);
    }
  }

  return orderedColumns;
};

export const getTrackedKeysFromSchemaFields = (schemaFields: FormSchemaField[]): Set<string> =>
  new Set(schemaFields.map((field) => `${getRoleFormKeyPrefix(field)}${field.name}`));

export const parseJsonArrayIds = (value: string | null): number[] => {
  if (!value) {
    return [];
  }
  try {
    const parsed = JSON.parse(value);

    if (Array.isArray(parsed)) {
      return parsed.filter((item): item is number => typeof item === "number" && item > 0);
    }
  } catch {
    // ignore malformed JSON
  }
  return [];
};

export const getNestedRoles = (
  values: ManageRolesMultiForm | undefined,
  rolesFormKey: RolesFormKey
): ManageRolesFormType[] | undefined => {
  const [, indexStr] = rolesFormKey.split(".");
  return values?.[FORM_SUBTEMPLATES_KEY]?.[Number(indexStr)]?.[FORM_ROLES_KEY];
};

export const getRoleKey = (role: Pick<ManageRolesFormType, "id" | "uniqueId">): Id =>
  role[FIELD_ID_KEY] ?? role.uniqueId;

export const buildRoleMap = (formValues: ManageRolesMultiForm): Map<Id, ManageRolesFormType> => {
  const map = new Map<Id, ManageRolesFormType>();
  for (const subtemplate of formValues.subtemplates ?? []) {
    for (const role of subtemplate.roles ?? []) {
      map.set(getRoleKey(role), role);
    }
  }
  return map;
};

export const mergeDraftRolesIntoForm = (
  formData: ManageRolesMultiForm,
  draftRoles: ManageRolesFormType[]
): ManageRolesMultiForm => {
  if (draftRoles.length === 0) {
    return formData;
  }

  const draftsBySubtemplate = new Map<string, ManageRolesFormType[]>();
  for (const draft of draftRoles) {
    const key = draft.subtemplate_key ?? NO_SUBTEMPLATE_VALUE;
    if (!draftsBySubtemplate.has(key)) {
      draftsBySubtemplate.set(key, []);
    }
    draftsBySubtemplate.get(key)!.push(draft);
  }

  const subtemplates = (formData[FORM_SUBTEMPLATES_KEY] ?? []).map((subtemplate) => {
    const key = subtemplate[FIELD_SUBTEMPLATE_KEY];
    const drafts = draftsBySubtemplate.get(key);
    if (!drafts) {
      return subtemplate;
    }
    draftsBySubtemplate.delete(key);
    return { ...subtemplate, [FORM_ROLES_KEY]: [...drafts, ...(subtemplate[FORM_ROLES_KEY] ?? [])] };
  });

  for (const [key, drafts] of draftsBySubtemplate.entries()) {
    subtemplates.push({ [FIELD_SUBTEMPLATE_KEY]: key, [FORM_ROLES_KEY]: drafts });
  }

  return { [FORM_SUBTEMPLATES_KEY]: subtemplates };
};

export const findRoleFormPath = (
  formValues: ManageRolesMultiForm,
  roleId: Id
): { subtemplateIndex: number; roleIndex: number } | undefined => {
  const subtemplates = formValues.subtemplates ?? [];

  const subtemplateIndex = subtemplates.findIndex((subtemplate) =>
    (subtemplate.roles ?? []).some((role) => getRoleKey(role) === roleId)
  );

  if (subtemplateIndex === -1) {
    return undefined;
  }

  const roleIndex = (subtemplates[subtemplateIndex].roles ?? []).findIndex(
    (role) => getRoleKey(role) === roleId
  );

  return { subtemplateIndex, roleIndex };
};

export const getLastNewRoleRowIndex = (roleKeys: Id[], newActivityIds: Id[]): number => {
  const newActivityIdSet = new Set(newActivityIds);
  const isNewRoleKey = (roleKey: Id) => {
    const isNewDraftRole = typeof roleKey === "string" && roleKey.startsWith(DRAFT_UNIQUE_ID_PREFIX);
    const isNewRole = newActivityIdSet.has(roleKey);
    return isNewDraftRole || isNewRole;
  };

  let lastNewRowIndex = -1;
  for (const roleKey of roleKeys) {
    if (!isNewRoleKey(roleKey)) {
      break;
    }
    lastNewRowIndex += 1;
  }

  return lastNewRowIndex;
};

const ROLE_ERROR_PATH_REGEX = new RegExp(
  `^(${FORM_SUBTEMPLATES_KEY}\\.\\d+\\.${FORM_ROLES_KEY}\\.\\d+)\\.(.+)$`
);

const flattenFieldErrors = (node: unknown, path = ""): string[] => {
  if (!node || typeof node !== "object") {
    return [];
  }

  if (typeof (node as { message?: unknown }).message === "string") {
    return [path];
  }

  return Object.entries(node).flatMap(([key, child]) =>
    key === "ref" ? [] : flattenFieldErrors(child, path ? `${path}.${key}` : key)
  );
};

export const getStaleFieldErrorPaths = (
  errors: unknown,
  previousValues: ManageRolesMultiForm,
  nextValues: ManageRolesMultiForm
): FieldPath<ManageRolesMultiForm>[] =>
  flattenFieldErrors(errors)
    .filter((path) => {
      const match = ROLE_ERROR_PATH_REGEX.exec(path);
      if (!match) {
        return true;
      }

      const [, roleFormKey, fieldKey] = match;
      const previousRole = get(previousValues, roleFormKey);
      const nextRole = get(nextValues, roleFormKey);
      const isSameRole = previousRole?.id !== undefined && previousRole.id === nextRole?.id;

      if (!isSameRole) {
        return true;
      }

      const previousValue = get(previousRole, fieldKey);
      const nextValue = get(nextRole, fieldKey);

      return !(isEqual(previousValue, nextValue) || (isEmptyValue(previousValue) && isEmptyValue(nextValue)));
    })
    .map((path) => path as FieldPath<ManageRolesMultiForm>);
